import { supabase } from "@/lib/supabase";
import { LeaderboardBoard } from "@/lib/rng";
import { WheelOption } from "@/data/wheels";

export type AlignmentLabel =
  | "Hero"
  | "Villain"
  | "Hero's Sidekick"
  | "Villain's Sidekick";

export interface RolledResult {
  wheelName: string;
  option: WheelOption;
}

export interface LeaderboardEntry {
  name: string;
  score: number;
  rank: string;
  board: LeaderboardBoard; // "hero" | "villain"
  alignmentLabel: string; // e.g. "Hero", "Villain's Sidekick"
  isSidekick: boolean;
  hasCrown: boolean;
  rolledResults: RolledResult[];
  userId?: string;
}

export interface ScoreRow {
  id: string;
  name: string;
  alignment: AlignmentLabel;
  score: number;
  rank_label: string;
  rolled_results: RolledResult[];
  has_crown: boolean;
  is_sidekick: boolean;
  created_at: string;
  expires_at: string | null;
  user_id: string | null;
  shared?: boolean;
  multiplayer?: boolean;
  // The player's CURRENT equipped flair, merged in from their profile at fetch
  // time (see attachCosmetics). Absent for guest rows and pre-migration.
  title?: string | null;
  color?: string | null;
}

// Merges each signed-in row's current equipped title/color onto the score rows
// so the leaderboard can show flair next to the name. One extra query for all
// the distinct user_ids. Fully defensive: if the cosmetics columns don't exist
// yet (migration not run) or anything fails, the rows are returned unchanged.
async function attachCosmetics(rows: ScoreRow[]): Promise<void> {
  const ids = Array.from(
    new Set(rows.map((r) => r.user_id).filter((id): id is string => !!id)),
  );
  if (ids.length === 0) return;
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, equipped_title, equipped_color")
      .in("id", ids);
    if (error || !data) return;
    const byId = new Map(
      data.map((p) => [
        p.id as string,
        {
          title: (p.equipped_title as string | null) ?? null,
          color: (p.equipped_color as string | null) ?? null,
        },
      ]),
    );
    for (const row of rows) {
      const flair = row.user_id ? byId.get(row.user_id) : undefined;
      if (flair) {
        row.title = flair.title;
        row.color = flair.color;
      }
    }
  } catch {
    /* cosmetics are optional flair — never block the leaderboard on them */
  }
}

// Records a finished run to the `scores` table. Guests get a 12h expiry via
// the table's default; signed-in profile users pass `isGuest: false` so their
// entry persists permanently and is linked to their account. Returns the new
// row's id so the result can be shared at /result/[id].
export async function submitScore(
  entry: LeaderboardEntry,
  isGuest = true,
  opts?: { multiplayer?: boolean },
): Promise<string> {
  const payload: Record<string, unknown> = {
    name: entry.name,
    alignment: entry.alignmentLabel,
    score: entry.score,
    rank_label: entry.rank,
    rolled_results: entry.rolledResults,
    has_crown: entry.hasCrown,
    is_sidekick: entry.isSidekick,
  };
  // "vs friend" results are saved to the player's profile but kept off the
  // public leaderboard (the leaderboard/aggregate queries filter these out).
  // Only set when true so ordinary solo/guest submits don't depend on the
  // column existing.
  if (opts?.multiplayer) payload.multiplayer = true;
  if (!isGuest) {
    payload.expires_at = null;
    payload.user_id = entry.userId;
  }
  const { data, error } = await supabase
    .from("scores")
    .insert(payload)
    .select("id")
    .single();
  if (error) throw error;
  return data.id as string;
}

// A signed-in player's past rolls, most recent first.
export async function fetchUserHistory(userId: string): Promise<ScoreRow[]> {
  const { data, error } = await supabase
    .from("scores")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return data ?? [];
}

const BOARD_ALIGNMENTS: Record<LeaderboardBoard, AlignmentLabel[]> = {
  hero: ["Hero", "Hero's Sidekick"],
  villain: ["Villain", "Villain's Sidekick"],
};

// Top 100 entries for a board, excluding expired guest rows.
export async function fetchLeaderboard(
  board: LeaderboardBoard,
): Promise<ScoreRow[]> {
  const { data, error } = await supabase
    .from("scores")
    .select("*")
    .in("alignment", BOARD_ALIGNMENTS[board])
    .eq("multiplayer", false)
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order("score", { ascending: false })
    .limit(100);
  if (error) throw error;
  const rows = data ?? [];
  await attachCosmetics(rows);
  return rows;
}

// Sum of every (non-expired) score on a board, for the team-vs-team total.
export async function fetchAggregateScore(
  board: LeaderboardBoard,
): Promise<number> {
  const { data, error } = await supabase
    .from("scores")
    .select("score")
    .in("alignment", BOARD_ALIGNMENTS[board])
    .eq("multiplayer", false)
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);
  if (error) throw error;
  return (data ?? []).reduce((sum, row) => sum + row.score, 0);
}
